/* eslint-disable no-unused-vars */

import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";

import { useHistory, useLocation } from "react-router-dom";

import { makeStyles, alpha, useTheme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import SearchIcon from "@material-ui/icons/Search";
import Tooltip from "@material-ui/core/Tooltip";

import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import duplicaCampanha from "./duplicaCampanha";
import DescriptionIcon from "@material-ui/icons/Description";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import RepeatIcon from "@material-ui/icons/Repeat";
import StopIcon from "@material-ui/icons/Stop";
import ScheduleIcon from "@material-ui/icons/Schedule";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";

import MainContainer from "../../components/MainContainer";
import CampaignsNav from "../../components/CampaignsNav";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import CampaignModal from "../../components/CampaignModal";
import CampaignsConfigModal from "../../components/CampaignsConfigModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import AerosConfirmModal from "../../components/AerosConfirmModal";
import toastError from "../../errors/toastError";
import { Select, MenuItem } from "@material-ui/core";
import { isArray } from "lodash";
import { useDate } from "../../hooks/useDate";
import ForbiddenPage from "../../components/ForbiddenPage";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import { AEROS, stateText } from "../../styles/aerosTokens";
import useCampaignPageStyles from "./campaignPageStyles";

const reducer = (state, action) => {
  if (action.type === "LOAD_CAMPAIGNS") {
    const campaigns = action.payload;
    const newCampaigns = [];

    if (isArray(campaigns)) {
      campaigns.forEach((campaign) => {
        const campaignIndex = state.findIndex((u) => u.id === campaign.id);
        if (campaignIndex !== -1) {
          state[campaignIndex] = campaign;
        } else {
          newCampaigns.push(campaign);
        }
      });
    }

    return [...state, ...newCampaigns];
  }

  if (action.type === "UPDATE_CAMPAIGNS") {
    const campaign = action.payload;
    const campaignIndex = state.findIndex((u) => u.id === campaign.id);

    if (campaignIndex !== -1) {
      state[campaignIndex] = campaign;
      return [...state];
    } else {
      return [campaign, ...state];
    }
  }

  if (action.type === "DELETE_CAMPAIGN") {
    const campaignId = action.payload;

    const campaignIndex = state.findIndex((u) => u.id === campaignId);
    if (campaignIndex !== -1) {
      state.splice(campaignIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

// Grid da tabela — espelha o mockup Campanhas A:
// Nome | Status | Recorrência | Lista | Conexão | Agendamento | Próx. execução | Ações
// (coluna Confirmação removida: o texto invadia os ícones de ação; a
// confirmação continua visível e editável no modal da campanha)
const TABLE_GRID = "1.9fr 1.1fr 1.3fr 1.4fr 1.2fr 1.3fr 1.3fr 1.7fr";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
  // ---- header da página (60px, título + saldo SMS + ações à direita) ----
  filterPill: {
    backgroundColor: theme.palette.aeros.surface,
    border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
    borderRadius: 10,
    fontSize: 12.5,
    color: theme.palette.text.secondary,
    "& .MuiSelect-root": {
      padding: "8px 12px",
      paddingRight: 28,
      fontSize: 12.5,
      color: theme.palette.text.secondary,
      backgroundColor: "transparent",
    },
    "& .MuiSelect-icon": {
      color: theme.palette.text.secondary,
      fontSize: 18,
      right: 6,
    },
  },
  settingsButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: theme.palette.aeros.surface,
    border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
    color: theme.palette.text.secondary,
    padding: 0,
    "&:hover": {
      color: theme.palette.text.primary,
      borderColor: alpha(AEROS.cyan, 0.4),
      backgroundColor: "transparent",
    },
    "& .MuiSvgIcon-root": { fontSize: 18 },
  },
  // ---- corpo da página ----
  // ---- card da tabela em CSS grid ----
  cellFirst: {
    paddingLeft: 20,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 6,
    "& .MuiSvgIcon-root": { fontSize: 15 },
  },
  // ---- pills de recorrência ----
  recPill: {
    display: "inline-block",
    fontSize: 10.5,
    fontWeight: 600,
    borderRadius: 99,
    padding: "2px 9px",
  },
  recPillRecurring: {
    backgroundColor: alpha("#C08BFF", 0.12),
    border: "1px solid rgba(160,68,255,0.35)",
    color: stateText(theme, "#C08BFF", "purple"),
  },
  recPillSingle: {
    backgroundColor: "transparent",
    border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.15)"}`,
    color: theme.palette.text.secondary,
  },
  // ---- rodapé/paginação ----
  tableFooter: {
    display: "flex",
    alignItems: "center",
    padding: "12px 20px",
    fontSize: 12,
    color: theme.palette.text.secondary,
    gap: 4,
  },
  pageSizeSelect: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    "& .MuiSelect-root": {
      fontSize: 12,
      color: theme.palette.text.secondary,
      paddingRight: 18,
      backgroundColor: "transparent",
    },
    "& .MuiSelect-icon": {
      color: theme.palette.text.secondary,
      fontSize: 16,
    },
  },
  pagination: {
    marginLeft: "auto",
    display: "flex",
    gap: 4,
  },
  pageBtn: {
    width: 28,
    height: 28,
    borderRadius: 8,
    border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
    background: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontFamily: "inherit",
    color: theme.palette.text.secondary,
    padding: 0,
    "&:hover:not(:disabled)": {
      borderColor: alpha(AEROS.cyan, 0.4),
      color: theme.palette.text.primary,
    },
    "&:disabled": {
      opacity: 0.4,
      cursor: "default",
    },
  },
  pageBtnActive: {
    border: "none",
    backgroundColor: alpha("#A044FF", 0.2),
    color: stateText(theme, "#C08BFF", "purple"),
    fontWeight: 700,
  },
  statusPillEmAndamento: {
    color: stateText(theme, AEROS.cyan, "info"),
    backgroundColor: alpha(AEROS.cyan, 0.1),
    border: `1px solid ${alpha(AEROS.cyan, 0.3)}`,
  },
  statusPillProgramada: {
    color: stateText(theme, "#C08BFF", "purple"),
    backgroundColor: alpha("#C08BFF", 0.12),
    border: "1px solid rgba(192,139,255,0.35)",
  },
  statusPillFinalizada: {
    color: stateText(theme, theme.palette.success.main, "success"),
    backgroundColor: alpha(theme.palette.success.main, 0.1),
    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
  },
  statusPillCancelada: {
    color: stateText(theme, theme.palette.error.main, "error"),
    backgroundColor: alpha(theme.palette.error.main, 0.1),
    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
  },
  statusPillInativa: {
    color: theme.palette.text.secondary,
    backgroundColor: alpha(theme.palette.text.primary, 0.06),
    border: `1px solid ${alpha(theme.palette.text.secondary, 0.3)}`,
  },
  executionCountCaption: {
    fontSize: 10.5,
    color: theme.palette.aeros.textTertiary,
    marginTop: 3,
  },
  smsBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 10px",
    borderRadius: 99,
    fontSize: 11,
    fontWeight: 600,
    color: stateText(theme, AEROS.cyan, "info"),
    backgroundColor: alpha(AEROS.cyan, 0.1),
    border: `1px solid ${alpha(AEROS.cyan, 0.3)}`,
  },
  nextExecutionCell: {
    fontSize: 12.5,
    display: "flex",
    alignItems: "center",
    gap: 4,
    "& .MuiSvgIcon-root": { fontSize: 14, color: stateText(theme, AEROS.cyan, "info") },
  },
  };
});

const Campaigns = () => {
  const classes = { ...useCampaignPageStyles({ grid: TABLE_GRID }), ...useStyles() };
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  // Deep link preservado: /campaigns-config renderiza esta página com o
  // modal de configurações já aberto (rota antiga virou modal).
  const [configModalOpen, setConfigModalOpen] = useState(
    () => location.pathname === "/campaigns-config"
  );
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [stopRecurrenceModalOpen, setStopRecurrenceModalOpen] = useState(false);
  // Confirmações Aeros: stats da campanha para o texto ("já entregou X de Y").
  const [cancelling, setCancelling] = useState(null);
  const [cancelStats, setCancelStats] = useState(null);
  const [deleteStats, setDeleteStats] = useState(null);
  const askCancel = (campaign) => { setCancelStats(null); setCancelling(campaign); api.get(`/campaigns/${campaign.id}/stats`).then(({ data }) => setCancelStats(data)).catch(() => {}); };
  const askDelete = (campaign) => { setDeleteStats(null); setDeletingCampaign(campaign); setConfirmModalOpen(true); api.get(`/campaigns/${campaign.id}/stats`).then(({ data }) => setDeleteStats(data)).catch(() => {}); };
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [recurrenceFilter, setRecurrenceFilter] = useState("");
  const [campaigns, dispatch] = useReducer(reducer, []);
  const [smsSaldo, setSmsSaldo] = useState(null);
  const { user, socket } = useContext(AuthContext);

  const { datetimeToClient } = useDate();
  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useCampaigns) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    const fetchSmsSaldo = async () => {
      try {
        const { data } = await api.get("/sms/saldo");
        setSmsSaldo(data.saldo);
      } catch (err) {
        // Falha silenciosa: tenant sem SMS habilitado ou endpoint indisponível.
        setSmsSaldo(null);
      }
    };
    fetchSmsSaldo();
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, statusFilter, recurrenceFilter, pageSize]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchCampaigns();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, statusFilter, recurrenceFilter, pageSize]);

  useEffect(() => {
    const companyId = user.companyId;

    const onCompanyCampaign = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CAMPAIGNS", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CAMPAIGN", payload: +data.id });
      }
    }

    socket.on(`company-${companyId}-campaign`, onCompanyCampaign);
    return () => {
      socket.off(`company-${companyId}-campaign`, onCompanyCampaign);
    };
  }, [user]);

  const fetchCampaigns = async () => {
    try {
      const { data } = await api.get("/campaigns/", {
        params: { 
          searchParam, 
          pageNumber, 
          pageSize,
          status: statusFilter,
          isRecurring: recurrenceFilter 
        },
      });
      
      // Para paginação real, substituir os dados em vez de adicionar
      if (pageNumber === 1) {
        dispatch({ type: "RESET" });
      }
      dispatch({ type: "LOAD_CAMPAIGNS", payload: data.records });
      
      setHasMore(data.hasMore);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.count || 0);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(true);
  };

  const handleCloseCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(false);
  };

  const handleCloseConfigModal = () => {
    setConfigModalOpen(false);
    if (location.pathname === "/campaigns-config") {
      history.replace("/campaigns");
    }
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleRecurrenceFilterChange = (event) => {
    setRecurrenceFilter(event.target.value);
  };

  // Duplicar: o backend cria a cópia INATIVA e sem agendamento; o modal abre
  // já na cópia para o usuário editar o que quiser antes de agendar.
  const [duplicandoId, setDuplicandoId] = useState(null);
  const handleDuplicateCampaign = async (campaign) => {
    if (duplicandoId) return;
    setDuplicandoId(campaign.id);
    try {
      const copia = await duplicaCampanha(api, campaign);
      toast.success(i18n.t("aeros.campaign.duplicate.success"));
      handleEditCampaign(copia);
    } catch (err) {
      toastError(err);
    } finally {
      setDuplicandoId(null);
    }
  };

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setCampaignModalOpen(true);
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/campaigns/${campaignId}`);
      toast.success(i18n.t("campaigns.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingCampaign(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleStopRecurrence = async (campaignId) => {
    try {
      await api.post(`/campaigns/${campaignId}/stop-recurrence`);
      toast.success(i18n.t("aeros.campaign.stopRecurrence.toast"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toastError(err);
    }
    setStopRecurrenceModalOpen(false);
    setSelectedCampaign(null);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPageNumber(1);
  };

  // Janela de até 5 páginas centrada na atual, para os quadrados de paginação.
  const pageWindow = (() => {
    const start = Math.max(1, Math.min(pageNumber - 2, totalPages - 4));
    const end = Math.min(totalPages, start + 4);
    const pages = [];
    for (let p = start; p <= end; p++) pages.push(p);
    return pages;
  })();

  const formatStatus = (val) => {
    switch (val) {
      case "INATIVA":
        return "Inativa";
      case "PROGRAMADA":
        return "Programada";
      case "EM_ANDAMENTO":
        return "Em Andamento";
      case "CANCELADA":
        return "Cancelada";
      case "FINALIZADA":
        return "Finalizada";
      default:
        return val;
    }
  };

  const getStatusPillClass = (status) => {
    switch (status) {
      case "INATIVA":
        return classes.statusPillInativa;
      case "PROGRAMADA":
        return classes.statusPillProgramada;
      case "EM_ANDAMENTO":
        return classes.statusPillEmAndamento;
      case "CANCELADA":
        return classes.statusPillCancelada;
      case "FINALIZADA":
        return classes.statusPillFinalizada;
      default:
        return classes.statusPillInativa;
    }
  };

  const formatRecurrenceType = (type) => {
    switch (type) {
      case "minutely":
        return "Por Minuto";
      case "hourly":
        return "Por Hora";
      case "daily":
        return "Diário";
      case "weekly":
        return "Semanal";
      case "biweekly":
        return "Quinzenal";
      case "monthly":
        return "Mensal";
      case "yearly":
        return "Anual";
      default:
        return type;
    }
  };

  const cancelCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <MainContainer>
      <AerosConfirmModal
        open={confirmModalOpen && Boolean(deletingCampaign)}
        onClose={() => { setConfirmModalOpen(false); setDeletingCampaign(null); }}
        onConfirm={async () => { await handleDeleteCampaign(deletingCampaign.id); setConfirmModalOpen(false); fetchCampaigns(); }}
        icon={<DeleteOutlineIcon />}
        tone="danger"
        title={i18n.t("aeros.campaign.confirmDelete.title", { name: deletingCampaign?.name || "" })}
        description={deleteStats ? i18n.t("aeros.campaign.confirmDelete.text", { executions: deletingCampaign?.executionCount || 1, shipping: (deleteStats.deliveredMessages || 0) + (deleteStats.failedMessages || 0) + (deleteStats.pendingMessages || 0) }) : i18n.t("aeros.campaign.confirmDelete.textSimple")}
        confirmLabel={i18n.t("aeros.campaign.confirmDelete.confirm")}
      />
      <AerosConfirmModal
        open={Boolean(cancelling)}
        onClose={() => setCancelling(null)}
        onConfirm={async () => { await cancelCampaign(cancelling); setCancelling(null); }}
        icon={<PauseCircleOutlineIcon />}
        tone="warning"
        title={i18n.t(cancelling?.status === "PROGRAMADA" ? "aeros.campaign.confirmCancel.titleScheduled" : "aeros.campaign.confirmCancel.title")}
        description={cancelling?.status === "PROGRAMADA"
          ? i18n.t("aeros.campaign.confirmCancel.textScheduled", { name: cancelling?.name || "" })
          : i18n.t("aeros.campaign.confirmCancel.text", { name: cancelling?.name || "", delivered: cancelStats?.deliveredMessages || 0, total: cancelStats?.totalMessages || 0, remaining: Math.max(0, (cancelStats?.totalMessages || 0) - (cancelStats?.deliveredMessages || 0) - (cancelStats?.failedMessages || 0)) })}
        confirmLabel={i18n.t("aeros.campaign.confirmCancel.confirm")}
        cancelLabel={i18n.t(cancelling?.status === "PROGRAMADA" ? "aeros.campaign.confirmCancel.keepScheduled" : "aeros.campaign.confirmCancel.keep")}
      />

      <ConfirmationModal
        title={i18n.t("aeros.campaign.stopRecurrence.title")}
        open={stopRecurrenceModalOpen}
        onClose={() => setStopRecurrenceModalOpen(false)}
        onConfirm={() => handleStopRecurrence(selectedCampaign?.id)}
        confirmLabel={i18n.t("aeros.campaign.stopRecurrence.confirm")}
        cancelLabel={i18n.t("aeros.campaign.stopRecurrence.keep")}
      >
        {/* Em andamento, o backend cancela a execucao atual na hora
            (StopRecurrenceService); agendada, vira finalizada. */}
        {i18n.t(selectedCampaign?.status === "EM_ANDAMENTO"
          ? "aeros.campaign.stopRecurrence.textRunning"
          : "aeros.campaign.stopRecurrence.textScheduled")}
      </ConfirmationModal>

      <CampaignsConfigModal
        open={configModalOpen}
        onClose={handleCloseConfigModal}
      />

      {campaignModalOpen && (
        <CampaignModal
          resetPagination={() => {
            setPageNumber(1);
            fetchCampaigns();
          }}
          open={campaignModalOpen}
          onClose={handleCloseCampaignModal}
          aria-labelledby="form-dialog-title"
          campaignId={selectedCampaign && selectedCampaign.id}
        />
      )}
      {
        user.profile === "user" && user?.showCampaign === "disabled" ?
          <ForbiddenPage />
          :
          <>
            <div className={classes.pageHeader}>
              <Typography component="div" className={classes.pageTitle}>
                {i18n.t("campaigns.title")}
              </Typography>
              {smsSaldo !== null && (
                <span className={classes.smsBadge}>
                  Saldo SMS: {smsSaldo}
                </span>
              )}
              <div className={classes.headerActions}>
                <InputBase
                  className={classes.searchPill}
                  placeholder="Buscar campanha…"
                  type="search"
                  value={searchParam}
                  onChange={handleSearch}
                  startAdornment={<SearchIcon />}
                  inputProps={{ "aria-label": i18n.t("campaigns.searchPlaceholder") }}
                />
                <Select
                  className={classes.filterPill}
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  disableUnderline
                  displayEmpty
                  renderValue={(v) => `Status: ${v === "" ? "Todos" : formatStatus(v)}`}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="INATIVA">Inativa</MenuItem>
                  <MenuItem value="PROGRAMADA">Programada</MenuItem>
                  <MenuItem value="EM_ANDAMENTO">Em Andamento</MenuItem>
                  <MenuItem value="CANCELADA">Cancelada</MenuItem>
                  <MenuItem value="FINALIZADA">Finalizada</MenuItem>
                </Select>
                <Select
                  className={classes.filterPill}
                  value={recurrenceFilter}
                  onChange={handleRecurrenceFilterChange}
                  disableUnderline
                  displayEmpty
                  renderValue={(v) =>
                    `Recorrência: ${v === "" ? "Todas" : v === "true" ? "Recorrentes" : "Únicas"}`
                  }
                >
                  <MenuItem value="">Todas</MenuItem>
                  <MenuItem value="true">Recorrentes</MenuItem>
                  <MenuItem value="false">Únicas</MenuItem>
                </Select>
                <Tooltip title={i18n.t("campaignsConfig.title")}>
                  <IconButton
                    className={classes.settingsButton}
                    onClick={() => setConfigModalOpen(true)}
                    aria-label={i18n.t("campaignsConfig.title")}
                  >
                    <SettingsOutlinedIcon />
                  </IconButton>
                </Tooltip>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.gradientButton}
                  startIcon={<AddIcon />}
                  onClick={handleOpenCampaignModal}
                >
                  {i18n.t("campaigns.buttons.add")}
                </Button>
              </div>
            </div>

            <div className={classes.pageBody}>
              <CampaignsNav />
              <div className={classes.tableCard}>
                <div className={classes.tableHeadRow}>
                  <div className={classes.tableHeadCell} style={{ paddingLeft: 20 }}>
                    {i18n.t("campaigns.table.name")}
                  </div>
                  <div className={classes.tableHeadCell}>
                    {i18n.t("campaigns.table.status")}
                  </div>
                  <div className={classes.tableHeadCell}>Recorrência</div>
                  <div className={classes.tableHeadCell}>
                    {i18n.t("campaigns.table.contactList")}
                  </div>
                  <div className={classes.tableHeadCell}>
                    {i18n.t("campaigns.table.whatsapp")}
                  </div>
                  <div className={classes.tableHeadCell}>
                    {i18n.t("campaigns.table.scheduledAt")}
                  </div>
                  <div className={classes.tableHeadCell}>Próx. execução</div>
                  <div className={`${classes.tableHeadCell} ${classes.cellActionsHead}`}>
                    {i18n.t("campaigns.table.actions")}
                  </div>
                </div>
                {campaigns.map((campaign) => (
                  <div className={classes.tableRow} key={campaign.id}>
                    <div className={`${classes.tableCell} ${classes.cellFirst}`}>
                      {campaign.isRecurring && (
                        <Tooltip title="Campanha Recorrente">
                          <RepeatIcon
                            htmlColor={stateText(theme, "#C08BFF", "purple")}
                          />
                        </Tooltip>
                      )}
                      {campaign.name}
                    </div>
                    <div className={classes.tableCell}>
                      <span className={`${classes.statusPill} ${getStatusPillClass(campaign.status)}`}>
                        <span className={classes.statusDot} />
                        {formatStatus(campaign.status)}
                      </span>
                    </div>
                    <div className={classes.tableCell}>
                      <span
                        className={`${classes.recPill} ${
                          campaign.isRecurring
                            ? classes.recPillRecurring
                            : classes.recPillSingle
                        }`}
                      >
                        {campaign.isRecurring
                          ? formatRecurrenceType(campaign.recurrenceType)
                          : "Única"}
                      </span>
                      {campaign.isRecurring && (
                        <div className={classes.executionCountCaption}>
                          {campaign.executionCount || 0} execuções
                        </div>
                      )}
                    </div>
                    <div className={`${classes.tableCell} ${classes.cellMuted}`}>
                      {campaign.contactListId
                        ? campaign.contactList?.name || "Lista removida"
                        : campaign.tagListId && campaign.tagListId !== "Nenhuma"
                        ? `Tag: ${campaign.tagListId}`
                        : "Não definida"}
                    </div>
                    <div className={`${classes.tableCell} ${classes.cellMuted}`}>
                      {campaign.whatsappId
                        ? campaign.whatsapp?.name || "WhatsApp removido"
                        : "Não definido"}
                    </div>
                    <div className={`${classes.tableCell} ${classes.cellMuted}`}>
                      {campaign.scheduledAt
                        ? datetimeToClient(campaign.scheduledAt)
                        : "Sem agendamento"}
                    </div>
                    <div className={`${classes.tableCell} ${classes.nextExecutionCell}`}>
                      {campaign.isRecurring && campaign.nextScheduledAt ? (
                        <>
                          <ScheduleIcon />
                          {datetimeToClient(campaign.nextScheduledAt)}
                        </>
                      ) : campaign.isRecurring && campaign.status === "FINALIZADA" ? (
                        <span style={{ color: theme.palette.aeros.textTertiary }}>
                          Recorrência finalizada
                        </span>
                      ) : (
                        <span style={{ color: theme.palette.aeros.textTertiary }}>—</span>
                      )}
                    </div>
                    <div className={`${classes.tableCell} ${classes.cellActions}`}>
                            {(campaign.status === "EM_ANDAMENTO" || campaign.status === "PROGRAMADA") && (
                              <Tooltip title={campaign.status === "PROGRAMADA" ? i18n.t("aeros.campaign.cancelScheduled") : i18n.t("aeros.campaign.pause")}>
                                <IconButton
                                  onClick={() => askCancel(campaign)}
                                  size="small"
                                  className={`${classes.rowActionButton} ${classes.rowActionButtonDanger}`}
                                  aria-label={campaign.status === "PROGRAMADA" ? i18n.t("aeros.campaign.cancelScheduled") : i18n.t("aeros.campaign.pause")}
                                >
                                  <PauseCircleOutlineIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {campaign.status === "CANCELADA" && (
                              <Tooltip title="Reiniciar Campanha">
                                <IconButton
                                  onClick={() => restartCampaign(campaign)}
                                  size="small"
                                  className={classes.rowActionButton}
                                  aria-label="Reiniciar Campanha"
                                >
                                  <PlayCircleOutlineIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {campaign.isRecurring && ["PROGRAMADA", "EM_ANDAMENTO"].includes(campaign.status) && (
                              <Tooltip title={i18n.t("aeros.campaign.stopRecurrence.action")}>
                                <IconButton
                                  onClick={() => {
                                    setSelectedCampaign(campaign);
                                    setStopRecurrenceModalOpen(true);
                                  }}
                                  size="small"
                                  className={`${classes.rowActionButton} ${classes.rowActionButtonDanger}`}
                                  aria-label={i18n.t("aeros.campaign.stopRecurrence.action")}
                                >
                                  <StopIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Relatório">
                              <IconButton
                                onClick={() =>
                                  history.push(`/campaign/${campaign.id}/report`)
                                }
                                size="small"
                                className={classes.rowActionButton}
                                aria-label="Relatório"
                              >
                                <DescriptionIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={i18n.t("aeros.campaign.duplicate.action")}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDuplicateCampaign(campaign)}
                                  disabled={duplicandoId !== null}
                                  className={classes.rowActionButton}
                                  aria-label={i18n.t("aeros.campaign.duplicate.action")}
                                >
                                  <FileCopyOutlinedIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={() => handleEditCampaign(campaign)}
                                className={classes.rowActionButton}
                                aria-label="Editar"
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  askDelete(campaign);
                                }}
                                className={`${classes.rowActionButton} ${classes.rowActionButtonDanger}`}
                                aria-label="Excluir"
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Tooltip>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className={classes.loadingRow}>
                    <CircularProgress size={24} />
                  </div>
                )}

                <div className={classes.tableFooter}>
                  <span>Total: {totalCount} campanhas ·</span>
                  <Select
                    className={classes.pageSizeSelect}
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    disableUnderline
                    renderValue={(v) => `${v} por página`}
                  >
                    {[5, 10, 25, 50, 100].map((size) => (
                      <MenuItem key={size} value={size}>
                        {size} por página
                      </MenuItem>
                    ))}
                  </Select>
                  <div className={classes.pagination}>
                    <button
                      type="button"
                      className={classes.pageBtn}
                      disabled={pageNumber <= 1}
                      onClick={() => setPageNumber(pageNumber - 1)}
                      aria-label="Página anterior"
                    >
                      ‹
                    </button>
                    {pageWindow.map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={`${classes.pageBtn} ${
                          p === pageNumber ? classes.pageBtnActive : ""
                        }`}
                        aria-current={p === pageNumber ? "page" : undefined}
                        onClick={() => setPageNumber(p)}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      type="button"
                      className={classes.pageBtn}
                      disabled={pageNumber >= totalPages}
                      onClick={() => setPageNumber(pageNumber + 1)}
                      aria-label="Próxima página"
                    >
                      ›
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>}
    </MainContainer>
  );
};

export default Campaigns;